import { motion } from 'framer-motion';
import { ArrowRight, Play, Zap, Star } from 'lucide-react';

export const Hero = () => {
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  const stagger = {
    visible: { transition: { staggerChildren: 0.1 } },
  };

  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-testid="hero-section">
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[#25D366]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-[#25D366]/5 to-transparent rounded-full" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="text-left"
          >
            {/* Eyebrow */}
            <motion.div variants={fadeInUp} className="mb-6">
              <span className="inline-flex items-center gap-2 bg-[#25D366]/10 text-[#25D366] dark:bg-[#25D366]/20 px-4 py-2 rounded-full text-sm font-semibold">
                <span className="w-2 h-2 bg-[#25D366] rounded-full animate-pulse" />
                Live inside WhatsApp — no download needed
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 dark:text-white leading-tight mb-6"
            >
              Your personal PA
              <br />
              <span className="gradient-text">is one message away.</span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              variants={fadeInUp}
              className="text-lg text-slate-600 dark:text-slate-400 max-w-lg mb-8 leading-relaxed"
            >
              AssistlyAI manages your meetings, reminders, calendar and workday through simple WhatsApp messages. In English, Hindi or Hinglish. Available 24/7. Starting at ₹199/month.
            </motion.p>

            {/* CTAs */}
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 mb-8">
              <a
                href="https://wa.me/919981361957?text=Hi%20AssistlyAI!"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1DA851] text-white px-8 py-4 rounded-full text-base font-semibold transition-all duration-300 shadow-lg hover:shadow-green-500/30 hover:scale-[1.02]"
                data-testid="hero-cta-primary"
              >
                Add AssistlyAI to WhatsApp
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <button
                className="group flex items-center justify-center gap-2 bg-transparent border-2 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-all"
                data-testid="hero-cta-secondary"
              >
                <Play className="w-5 h-5 fill-current" />
                See it in action
              </button>
            </motion.div>

            {/* Trust Line */}
            <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-3 text-sm text-slate-500 dark:text-slate-500 mb-6">
              <span className="flex items-center gap-1">
                <Zap className="w-4 h-4 text-[#25D366]" />
                3 min setup
              </span>
              <span className="w-1 h-1 bg-slate-300 dark:bg-slate-600 rounded-full" />
              <span>No app download</span>
              <span className="w-1 h-1 bg-slate-300 dark:bg-slate-600 rounded-full" />
              <span>Cancel anytime</span>
            </motion.div>

            {/* Social Proof */}
            <motion.div variants={fadeInUp} className="flex items-center gap-3">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-800 border-2 border-white dark:border-slate-900 flex items-center justify-center text-xs font-semibold text-slate-600 dark:text-slate-300"
                  >
                    {String.fromCharCode(64 + i)}
                  </div>
                ))}
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  2,000+ Indian professionals trust AssistlyAI
                </span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, x: 50 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <div className="relative">
              {/* Phone Mockup */}
              <div className="relative z-10 bg-slate-900 rounded-[3rem] p-3 shadow-2xl shadow-slate-900/20 dark:shadow-black/50 mx-auto max-w-[320px]">
                <div className="bg-slate-800 rounded-[2.5rem] overflow-hidden">
                  {/* Phone Header */}
                  <div className="bg-[#075E54] px-4 py-3 flex items-center gap-3">
                    <img
                      src={`${process.env.PUBLIC_URL || ''}/assistlylogo.png`}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover bg-white shrink-0"
                    />
                    <div>
                      <p className="text-white font-semibold text-sm">AssistlyAI</p>
                      <p className="text-emerald-200 text-xs">Online</p>
                    </div>
                  </div>
                  
                  {/* Chat Content */}
                  <div className="bg-[#ECE5DD] dark:bg-slate-900 p-4 min-h-[400px] space-y-3">
                    {/* User Message */}
                    <div className="flex justify-end">
                      <div className="bg-[#DCF8C6] dark:bg-emerald-900 rounded-lg rounded-tr-none p-3 max-w-[80%]">
                        <p className="text-slate-800 dark:text-slate-100 text-sm">Remind me to call Priya at 3pm tomorrow</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 text-right mt-1">10:30 AM</p>
                      </div>
                    </div>
                    
                    {/* AssistlyAI Reply */}
                    <div className="flex justify-start">
                      <div className="bg-white dark:bg-slate-800 rounded-lg rounded-tl-none p-3 max-w-[80%] shadow-sm">
                        <p className="text-slate-800 dark:text-slate-100 text-sm">Done! I'll remind you tomorrow at 3:00 PM to call Priya. 📞</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 text-right mt-1">10:30 AM</p>
                      </div>
                    </div>

                    {/* User Message */}
                    <div className="flex justify-end">
                      <div className="bg-[#DCF8C6] dark:bg-emerald-900 rounded-lg rounded-tr-none p-3 max-w-[80%]">
                        <p className="text-slate-800 dark:text-slate-100 text-sm">What's my schedule for today?</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 text-right mt-1">10:31 AM</p>
                      </div>
                    </div>

                    {/* AssistlyAI Reply */}
                    <div className="flex justify-start">
                      <div className="bg-white dark:bg-slate-800 rounded-lg rounded-tl-none p-3 max-w-[85%] shadow-sm">
                        <p className="text-slate-800 dark:text-slate-100 text-sm font-medium mb-2">Here's your day:</p>
                        <div className="space-y-1.5 text-sm text-slate-700 dark:text-slate-200">
                          <p>📅 11:00 AM - Team standup</p>
                          <p>📊 2:00 PM - Review Q4 report</p>
                          <p>☎️ 4:30 PM - Client call</p>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 text-right mt-2">10:31 AM</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <motion.div
                animate={{ y: [-5, 5, -5] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -top-4 -right-4 bg-white dark:bg-slate-800 rounded-2xl p-4 shadow-xl"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                    <span>📅</span>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Meeting scheduled</p>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">Tomorrow, 3 PM</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                animate={{ y: [5, -5, 5] }}
                transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -bottom-4 -left-8 bg-white dark:bg-slate-800 rounded-2xl p-4 shadow-xl"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                    <span>✅</span>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Task completed</p>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">Send report</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
